import React, { useContext, useEffect, useMemo, useState } from "react";
import { Grid, useTheme, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import { useForm, FormProvider } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import {
	getDmsList,
	saveAssociations,
} from "../service/contractorSearch.actions";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import { setSnackbar } from "../../../../util/state/globalAction";
import { FormAutocomplete } from "../../common/form-fields-wrappers";
import { RFMContractorDataValueState } from "../model/state";
import CustomLoader from "../../../../util/component/CustomLoader";
import AssociatedDmsList from "./AssociatedDmsList";
import { IDms } from "../../models/models";

const AssociationContractorDms = () => {
	const { t } = useTranslation(["network", "translation"]);
	const theme = useTheme();
	const { dispatchGlobalData } = useContext(GlobalContext);
	const [dmsList, setDmsList] = useState([]);
	const [contractorDataState, setContractorDataState] = useRecoilState(
		RFMContractorDataValueState
	);
	const [toAssociate, setToAssociate] = useState<IDms[]>([]);

	const associationForm = useForm<{ dms: any }>({
		defaultValues: {
			dms: null,
		},
	});

	const unavailableDms = useMemo(() => {
		return toAssociate.map((dms: IDms) => dms.dmsIdentificationCode);
	}, [toAssociate]);

	/** Associate DMS */
	const associateDmsMutation = useMutation(saveAssociations, {
		onSuccess: (response: { associatedDms: IDms[] }) => {
			setContractorDataState(null);
			setToAssociate(response.associatedDms);
			dispatchGlobalData({
				type: setSnackbar,
				payload: {
					snackbar: {
						message: t("contractorMng.managerRFM.message.pairingSuccessful"),
						type: snackbarTypes.success,
						show: true,
						autoClose: true,
					},
				},
			});
		},
		onError: (error) => {
			dispatchGlobalData({
				type: setSnackbar,
				payload: {
					snackbar: {
						message: t("translation:error.tryLater"),
						type: snackbarTypes.error,
						show: true,
						autoClose: true,
					},
				},
			});
		},
	});

	const getDmsListMutation = useMutation({
		mutationFn: getDmsList,
		onSuccess: (res: any) => {
			setDmsList(res.msObjectsList);
		},
		onError: (error) => {
			dispatchGlobalData({
				type: setSnackbar,
				payload: {
					snackbar: {
						message: t("translation:error.tryLater"),
						type: snackbarTypes.error,
						show: true,
						autoClose: true,
					},
				},
			});
		},
	});

	const associateDms = (data: any) => {
		if (!!data.dms) {
			const dmsSelected = associationForm.getValues("dms");
			associationForm.reset();
			let newList: any[] = toAssociate.map((dms: IDms) => {
				return {
					bpCodeMs: dms.dmsIdentificationCode,
					royaltiesPercentage: dms.royaltiesPercentage,
					emailRoyalties: dms.emailRoyalties,
				};
			});
			newList.push({
				bpCodeMs: dmsSelected.code,
				royaltiesPercentage: null,
				emailRoyalties: null,
			});

			setToAssociate([]);

			associateDmsMutation.mutate({
				association: newList,
				bpCodeContractor: contractorDataState?.bpCode ?? "",
			} as any);
		}
	};

	const action = (param: any) => {
		if (!!param) {
			const dmsList = toAssociate;
			setToAssociate([]);
			switch (param.action) {
				case "delete":
					associateDmsMutation.mutate({
						association: dmsList
							.filter((dms: any) => dms.idDms !== param.idDms)
							.map((dms: IDms) => {
								return {
									bpCodeMs: dms.dmsIdentificationCode,
									royaltiesPercentage: !!dms.royaltiesPercentage
										? dms.royaltiesPercentage
										: null,
									emailRoyalties: !!dms.emailRoyalties
										? dms.emailRoyalties
										: null,
								};
							}),
						bpCodeContractor: contractorDataState?.bpCode ?? "",
					} as any);
					break;
				case "update":
					associateDmsMutation.mutate({
						association: param.list.map((dms: IDms) => {
							return {
								bpCodeMs: dms.dmsIdentificationCode,
								royaltiesPercentage: !!dms.royaltiesPercentage
									? dms.royaltiesPercentage
									: null,
								emailRoyalties: !!dms.emailRoyalties
									? dms.emailRoyalties
									: null,
							};
						}),
						bpCodeContractor: contractorDataState?.bpCode ?? "",
					} as any);
					break;
			}
		}
	};

	useEffect(() => {
		getDmsListMutation.mutate({ tenant: contractorDataState?.idTenant });
		const primaryDms = contractorDataState.associatedDms.filter(
			(dms: IDms) => !!dms.primaryDms
		);

		setToAssociate(primaryDms);
	}, []);

	return (
		<>
			<FormProvider {...associationForm}>
				<Grid
					container
					xs={12}
					columnSpacing={2}
					sx={{ marginY: theme.spacing(1) }}
				>
					<Grid item xs={6}>
						<FormAutocomplete
							control={associationForm.control}
							name="dms"
							options={dmsList}
							label={t(
								"contractorMng.managerRFM.associationContractorDms.label.dmsList"
							)}
							isOptionEqualToValue={(option: any, value: any) =>
								option?.code === value?.code
							}
							getOptionLabel={(opt: any) => opt?.description}
							getOptionDisabled={(option) =>
								unavailableDms.includes(option?.code)
							}
						/>
					</Grid>

					<Grid
						item
						xs={6}
						sx={{
							display: "flex",
							justifyContent: "start",
							alignItems: "flex-end",
						}}
					>
						<Button
							variant="contained"
							onClick={associationForm.handleSubmit(associateDms)}
							sx={{ marginLeft: theme.spacing(2) }}
						>
							{t(
								"contractorMng.managerRFM.associationContractorDms.button.associate"
							)}
						</Button>
					</Grid>
				</Grid>
			</FormProvider>

			<AssociatedDmsList list={toAssociate} action={action} fullInfo={true} />

			<CustomLoader
				loading={associateDmsMutation.isLoading || getDmsListMutation.isLoading}
			/>
		</>
	);
};

export default AssociationContractorDms;
